/**
 * Comprehensive Validation Schemas for All 15 Workflow Types
 * 
 * Schema-based validation following append-only architecture and GraphQL patterns
 * Supports both JSON Schema format and custom validation rules
 */

export interface ValidationRule {
    required?: boolean;
    type?: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'date';
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: RegExp;
    enum?: string[] | readonly string[];
    minProperties?: number;
    maxProperties?: number;
    format?: 'email' | 'url' | 'date' | 'date-time' | 'snowflake-id' | 'json' | 'dual-id'; // dual-id: supports both legacy and Snowflake ID formats
    custom?: (value: unknown) => string | null;
}

export interface ValidationSchema {
    [field: string]: ValidationRule;
}

// Enhanced Workflow Definition Schema
export const workflowDefinitionSchema: ValidationSchema = {
    name: {
        required: true,
        type: 'string',
        minLength: 1,
        maxLength: 100,
        pattern: /^[a-zA-Z0-9_\-\s]+$/,
        custom: (value: unknown) => {
            if (typeof value === 'string' && value.trim() !== value) {
                return 'Name cannot have leading/trailing whitespace';
            }
            return null;
        }
    },
    version: {
        required: true,
        type: 'string',
        pattern: /^\d+\.\d+\.\d+$/,
        custom: (value: unknown) => {
            if (typeof value === 'string' && !value.match(/^\d+\.\d+\.\d+$/)) {
                return 'Version must follow semantic versioning (e.g., 1.0.0)';
            }
            return null;
        }
    },
    initial_state: {
        required: true,
        type: 'string',
        minLength: 1
    },
    states: {
        required: true,
        type: 'object',
        minProperties: 1,
        custom: (value: unknown) => {
            if (!Array.isArray(value) && typeof value === 'object' && value !== null) {
                const states = Object.keys(value);
                const valueAsRecord = value as Record<string, { state_type?: string }>;
                if (!states.some(s => valueAsRecord[s]?.state_type === 'Initial')) {
                    return 'At least one Initial state is required';
                }
                if (!states.some(s => valueAsRecord[s]?.state_type === 'Final')) {
                    return 'At least one Final state is required';
                }
            }
            return null;
        }
    },
    description: {
        required: false,
        type: 'string',
        maxLength: 500
    }
};

export const taskSchema: ValidationSchema = {
    title: {
        required: true,
        type: 'string',
        minLength: 1,
        maxLength: 200
    },
    assignee: {
        required: false,
        type: 'string',
        pattern: /^[a-zA-Z0-9_\-@.]+$/
    },
    due_date: {
        required: false,
        type: 'string',
        format: 'date-time',
        custom: (value: unknown) => {
            if (typeof value === 'string' && new Date(value) <= new Date()) {
                return 'Due date must be in the future';
            }
            return null;
        }
    },
    priority: {
        required: true,
        enum: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
    },
    task_type: {
        required: true,
        enum: ['APPROVAL', 'REVIEW', 'ACK', 'HANDOFF']
    }
};

export const policySchema: ValidationSchema = {
    name: {
        required: true,
        type: 'string',
        minLength: 1,
        maxLength: 100
    },
    type: {
        required: true,
        enum: ['GUARD', 'AUTHORIZATION', 'BUSINESS_RULE', 'VALIDATION', 'COMPLIANCE']
    },
    scope: {
        required: true,
        enum: ['GLOBAL', 'TENANT', 'WORKFLOW', 'FIELD', 'ENTITY']
    },
    expression: {
        required: true,
        type: 'string',
        minLength: 1,
        custom: (value: unknown) => {
            if (typeof value === 'string' && (value.includes('{{') || value.includes('}}'))) {
                const openBraces = (value.match(/\{\{/g) || []).length;
                const closeBraces = (value.match(/\}\}/g) || []).length;
                if (openBraces !== closeBraces) {
                    return 'Unmatched template braces in expression';
                }
            }
            return null;
        }
    },
    priority: {
        required: true,
        type: 'number',
        min: 1,
        max: 1000
    },
    enforcement_mode: {
        required: true,
        enum: ['STRICT', 'WARN', 'DISABLED']
    }
};

export const scheduleSchema: ValidationSchema = {
    name: {
        required: true,
        type: 'string',
        minLength: 1,
        maxLength: 100
    },
    type: {
        required: true,
        enum: ['CRON', 'INTERVAL', 'DELAY']
    },
    cron_expression: {
        required: false,
        type: 'string',
        custom: (value: unknown) => {
            if (typeof value === 'string' && value) {
                const parts = value.split(' ');
                if (parts.length !== 5) {
                    return 'Cron expression must have exactly 5 parts: minute hour day month weekday';
                }
            }
            return null;
        }
    },
    interval_seconds: {
        required: false,
        type: 'number',
        min: 1,
        max: 86400
    },
    active: {
        required: true,
        type: 'boolean'
    }
};

// Validation Schema Registry
export const validationSchemas = {
    WorkflowDefinition: workflowDefinitionSchema,
    Task: taskSchema,
    Policy: policySchema,
    Schedule: scheduleSchema,
    Instance: {
        workflow_definition_key: { required: true, type: 'string', minLength: 1 },
        status: { required: true, enum: ['RUNNING', 'PAUSED', 'COMPLETED', 'FAILED', 'CANCELLED'] }
    },
    Resource: {
        name: { required: true, type: 'string', minLength: 1 },
        type: { required: true, enum: ['DATABASE', 'API', 'FILE_SYSTEM', 'CACHE', 'QUEUE'] }
    },
    Integration: {
        name: { required: true, type: 'string', minLength: 1 },
        type: { required: true, enum: ['WEBHOOK', 'MESSAGE_QUEUE', 'ADAPTER'] }
    },
    Escalation: {
        name: { required: true, type: 'string', minLength: 1 },
        type: { required: true, enum: ['TIMEOUT', 'RETRY', 'ERROR_HANDLER', 'COMPLIANCE_HANDLER'] }
    },
    Context: {
        name: { required: true, type: 'string', minLength: 1 }
    },
    Actor: {
        name: { required: true, type: 'string', minLength: 1 }
    },
    TransitionCatalog: {
        name: { required: true, type: 'string', minLength: 1 }
    },
    SignalTemplate: {
        name: { required: true, type: 'string', minLength: 1 }
    },
    Timeline: {
        event_type: { required: true, type: 'string' }
    },
    ExecutionResult: {
        status: { required: true, enum: ['SUCCESS', 'FAILURE', 'PARTIAL'] }
    },
    JobStatus: {
        status: { required: true, enum: ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED'] }
    }
} as const;

export interface ValidationError {
    field: string;
    message: string;
    value?: any;
    code?: string;
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
}

export function validateData(data: Record<string, unknown>, schema: ValidationSchema): ValidationResult {
    const errors: ValidationError[] = [];

    for (const [field, rules] of Object.entries(schema)) {
        const value = data[field];
        const fieldErrors = validateField(field, value, rules);
        errors.push(...fieldErrors);
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

export function validateField(field: string, value: unknown, rules: ValidationRule): ValidationError[] {
    const errors: ValidationError[] = [];

    if (rules.required && (value === undefined || value === null || value === '')) {
        errors.push({
            field,
            message: `${field} is required`,
            value
        });
        return errors;
    }

    if (!rules.required && (value === undefined || value === null || value === '')) {
        return errors;
    }

    if (rules.type && !validateType(value, rules.type)) {
        errors.push({
            field,
            message: `${field} must be of type ${rules.type}`,
            value
        });
    }

    if (rules.type === 'string' && typeof value === 'string') {
        if (rules.minLength !== undefined && value.length < rules.minLength) {
            errors.push({
                field,
                message: `${field} must be at least ${rules.minLength} characters`,
                value
            });
        }

        if (rules.maxLength !== undefined && value.length > rules.maxLength) {
            errors.push({
                field,
                message: `${field} must be no more than ${rules.maxLength} characters`,
                value
            });
        }

        if (rules.pattern && !rules.pattern.test(value)) {
            errors.push({
                field,
                message: `${field} format is invalid`,
                value
            });
        }
    }

    if (rules.type === 'number' && typeof value === 'number') {
        if (rules.min !== undefined && value < rules.min) {
            errors.push({
                field,
                message: `${field} must be at least ${rules.min}`,
                value
            });
        }

        if (rules.max !== undefined && value > rules.max) {
            errors.push({
                field,
                message: `${field} must be no more than ${rules.max}`,
                value
            });
        }
    }

    if (rules.enum && !(rules.enum as readonly string[]).includes(String(value))) {
        errors.push({
            field,
            message: `${field} must be one of: ${rules.enum.join(', ')}`,
            value
        });
    }

    if (rules.custom) {
        const customError = rules.custom(value);
        if (customError) {
            errors.push({
                field,
                message: customError,
                value
            });
        }
    }

    return errors;
}

function validateType(value: unknown, type: string): boolean {
    switch (type) {
        case 'string':
            return typeof value === 'string';
        case 'number':
            return typeof value === 'number' && !isNaN(value);
        case 'boolean':
            return typeof value === 'boolean';
        case 'object':
            return typeof value === 'object' && value !== null && !Array.isArray(value);
        case 'array':
            return Array.isArray(value);
        case 'date':
            return value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)));
        default:
            return true;
    }
}

export function getValidationSchema(workflowType: keyof typeof validationSchemas): ValidationSchema {
    return validationSchemas[workflowType] || {};
}

// Legacy schemas for backward compatibility

// Complex Process Schema
export const ComplexProcessSchema = {
    type: "object",
    properties: {
        name: {
            type: "string",
            minLength: 1,
            maxLength: 100,
            pattern: "^[a-zA-Z0-9\\s\\-_]+$",
            description: "Process name must be 1-100 characters, alphanumeric with spaces, hyphens, underscores",
            // MANDATORY: Life-Critical Security Keywords
            xssSafe: true,
            sqlSafe: true,
            hipaaCompliant: true,
            soxAuditRequired: true,
            fdaDataIntegrity: true
        },
        domain: {
            type: "string",
            enum: ["Healthcare", "Finance", "Supply Chain", "HR", "IT", "Manufacturing", "Insurance", "Other"],
            description: "Business domain classification"
        },
        description: {
            type: "string",
            minLength: 10,
            maxLength: 500,
            description: "Process description must be 10-500 characters",
            // MANDATORY: Life-Critical Security Keywords
            xssSafe: true,
            sqlSafe: true,
            hipaaCompliant: true,
            soxAuditRequired: true,
            fdaDataIntegrity: true
        },
        complexity: {
            type: "string",
            enum: ["Low", "Medium", "High", "Very High"],
            description: "Process complexity level"
        },
        stages: {
            type: "array",
            minItems: 1,
            maxItems: 50,
            items: {
                $ref: "#/$defs/ProcessStage"
            },
            description: "Process must have 1-50 stages"
        },
        connections: {
            type: "array",
            items: {
                $ref: "#/$defs/ProcessConnection"
            }
        },
        actors: {
            type: "array",
            items: {
                type: "string",
                minLength: 1,
                maxLength: 50
            },
            uniqueItems: true,
            minItems: 1,
            description: "Must have at least one actor"
        },
        externalSystems: {
            type: "array",
            items: {
                $ref: "#/$defs/ExternalSystem"
            }
        },
        isTemplate: {
            type: "boolean",
            description: "Whether this is a template process"
        }
    },
    required: ["name", "domain", "description", "complexity", "stages", "actors"],
    additionalProperties: false,
    // MANDATORY: Life-Critical Security Validation for entire object
    lifeCriticalSecurity: true,
    $defs: {
        ProcessStage: {
            type: "object",
            properties: {
                id: {
                    type: "string",
                    pattern: "^[a-zA-Z0-9_-]+$"
                },
                name: {
                    type: "string",
                    minLength: 1,
                    maxLength: 100
                },
                stageType: {
                    type: "string",
                    enum: ["Entry", "Process", "Decision", "External", "Loop", "Meta", "Exit"]
                },
                actors: {
                    type: "array",
                    items: {
                        type: "string"
                    },
                    minItems: 1
                },
                steps: {
                    type: "array",
                    items: {
                        $ref: "#/$defs/ProcessStep"
                    }
                },
                loops: {
                    type: "boolean"
                },
                estimatedDuration: {
                    type: "string",
                    pattern: "^\\d+\\s*(minutes?|hours?|days?|weeks?)$"
                }
            },
            required: ["id", "name", "stageType", "actors"],
            additionalProperties: false
        },
        ProcessStep: {
            type: "object",
            properties: {
                id: {
                    type: "string",
                    pattern: "^[a-zA-Z0-9_-]+$"
                },
                name: {
                    type: "string",
                    minLength: 1,
                    maxLength: 100
                },
                actor: {
                    type: "string",
                    minLength: 1
                },
                stepType: {
                    type: "string",
                    enum: ["Manual", "Automated", "Approval", "Review", "Integration", "Transform", "Decision", "Escalation"]
                },
                external: {
                    type: "boolean"
                },
                conditional: {
                    type: "boolean"
                },
                loops: {
                    type: "boolean"
                },
                triggers: {
                    type: "array",
                    items: {
                        type: "string"
                    }
                },
                outputs: {
                    type: "array",
                    items: {
                        type: "string"
                    }
                },
                estimatedDuration: {
                    type: "string",
                    pattern: "^\\d+\\s*(minutes?|hours?|days?)$"
                }
            },
            required: ["id", "name", "actor", "stepType"],
            additionalProperties: false
        },
        ProcessConnection: {
            type: "object",
            properties: {
                from: {
                    type: "string",
                    minLength: 1
                },
                to: {
                    type: "string",
                    minLength: 1
                },
                connectionType: {
                    type: "string",
                    enum: ["Sequential", "Loop", "Conditional", "Parallel", "Escalation"]
                },
                condition: {
                    type: "string"
                },
                probability: {
                    type: "number",
                    minimum: 0,
                    maximum: 1
                }
            },
            required: ["from", "to", "connectionType"],
            additionalProperties: false
        },
        ExternalSystem: {
            type: "object",
            properties: {
                id: {
                    type: "string",
                    pattern: "^[a-zA-Z0-9_-]+$"
                },
                name: {
                    type: "string",
                    minLength: 1,
                    maxLength: 100
                },
                systemType: {
                    type: "string",
                    enum: ["API", "Database", "File", "Queue", "Service", "Other"]
                },
                integrationPoints: {
                    type: "array",
                    items: {
                        type: "string"
                    }
                },
                dataFormats: {
                    type: "array",
                    items: {
                        type: "string",
                        enum: ["JSON", "XML", "CSV", "EDI", "HL7", "FHIR", "Other"]
                    }
                },
                authentication: {
                    type: "string",
                    enum: ["None", "API Key", "OAuth", "Basic Auth", "Certificate", "Other"]
                }
            },
            required: ["id", "name", "systemType"],
            additionalProperties: false
        }
    }
} as const;

// Signal Schema
export const SignalSchema = {
    type: "object",
    properties: {
        instanceId: {
            type: "string",
            minLength: 1,
            pattern: "^[a-zA-Z0-9_-]+$"
        },
        signalName: {
            type: "string",
            minLength: 1,
            maxLength: 100,
            pattern: "^[a-zA-Z0-9_-]+$"
        },
        payload: {
            type: "object",
            description: "Signal payload data"
        },
        metadata: {
            type: "object",
            properties: {
                source: {
                    type: "string",
                    minLength: 1
                },
                correlationId: {
                    type: "string",
                    pattern: /^([a-z]{3}_[0-9A-Za-z]{10,11}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/ // Dual format: Snowflake ID or legacy format
                },
                causationId: {
                    type: "string",
                    pattern: /^([a-z]{3}_[0-9A-Za-z]{10,11}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/ // Dual format: Snowflake ID or legacy format
                },
                userId: {
                    type: "string"
                },
                timestamp: {
                    type: "string",
                    format: "date-time"
                },
                tags: {
                    type: "object",
                    additionalProperties: {
                        type: "string"
                    }
                }
            },
            required: ["source", "timestamp"],
            additionalProperties: false
        }
    },
    required: ["instanceId", "signalName"],
    additionalProperties: false
} as const;

// Schedule Schema
export const ScheduleSchema = {
    type: "object",
    properties: {
        name: {
            type: "string",
            minLength: 1,
            maxLength: 100
        },
        description: {
            type: "string",
            maxLength: 500
        },
        cronExpression: {
            type: "string",
            pattern: "^(@(annually|yearly|monthly|weekly|daily|hourly|reboot))|(@every (\\d+(ns|us|µs|ms|s|m|h))+)|((((\\d+,)+\\d+|(\\d+([/\\-])\\d+)|\\d+|\\*) ?){5,7})$"
        },
        timezone: {
            type: "string",
            pattern: "^[A-Za-z_/]+$"
        },
        enabled: {
            type: "boolean"
        },
        targetWorkflow: {
            type: "string",
            minLength: 1
        },
        payload: {
            type: "object"
        }
    },
    required: ["name", "cronExpression", "timezone", "targetWorkflow"],
    additionalProperties: false
} as const;

// Integration Schema
export const IntegrationSchema = {
    type: "object",
    properties: {
        name: {
            type: "string",
            minLength: 1,
            maxLength: 100
        },
        type: {
            type: "string",
            enum: ["api", "webhook", "database", "queue", "file"]
        },
        config: {
            type: "object",
            properties: {
                endpoint: {
                    type: "string",
                    format: "uri"
                },
                method: {
                    type: "string",
                    enum: ["GET", "POST", "PUT", "PATCH", "DELETE"]
                },
                headers: {
                    type: "object",
                    additionalProperties: {
                        type: "string"
                    }
                },
                authentication: {
                    type: "object",
                    properties: {
                        type: {
                            type: "string",
                            enum: ["none", "basic", "bearer", "oauth", "apikey"]
                        },
                        credentials: {
                            type: "object"
                        }
                    },
                    required: ["type"]
                },
                timeout: {
                    type: "number",
                    minimum: 1000,
                    maximum: 300000
                },
                retryPolicy: {
                    type: "object",
                    properties: {
                        maxAttempts: {
                            type: "number",
                            minimum: 1,
                            maximum: 10
                        },
                        backoffStrategy: {
                            type: "string",
                            enum: ["linear", "exponential"]
                        },
                        initialDelay: {
                            type: "number",
                            minimum: 100
                        }
                    },
                    required: ["maxAttempts", "backoffStrategy", "initialDelay"]
                }
            }
        }
    },
    required: ["name", "type", "config"],
    additionalProperties: false
} as const;

// Policy Schema (for business rules)
export const PolicySchema = {
    type: "object",
    properties: {
        name: {
            type: "string",
            minLength: 1,
            maxLength: 100,
            // MANDATORY: Life-Critical Security Keywords
            xssSafe: true,
            sqlSafe: true,
            hipaaCompliant: true,
            soxAuditRequired: true,
            fdaDataIntegrity: true
        },
        description: {
            type: "string",
            maxLength: 500,
            // MANDATORY: Life-Critical Security Keywords
            xssSafe: true,
            sqlSafe: true,
            hipaaCompliant: true,
            soxAuditRequired: true,
            fdaDataIntegrity: true
        },
        category: {
            type: "string",
            enum: ["Compliance", "Security", "Business Rule", "Validation", "Authorization"]
        },
        priority: {
            type: "number",
            minimum: 1,
            maximum: 100
        },
        expression: {
            type: "string",
            minLength: 1,
            description: "CEL expression for policy evaluation"
        },
        conditions: {
            type: "array",
            items: {
                type: "object",
                properties: {
                    field: {
                        type: "string"
                    },
                    operator: {
                        type: "string",
                        enum: ["equals", "notEquals", "greaterThan", "lessThan", "contains", "startsWith", "endsWith", "regex"]
                    },
                    value: {
                        oneOf: [
                            { type: "string" },
                            { type: "number" },
                            { type: "boolean" }
                        ]
                    }
                },
                required: ["field", "operator", "value"]
            }
        },
        actions: {
            type: "array",
            items: {
                type: "object",
                properties: {
                    type: {
                        type: "string",
                        enum: ["allow", "deny", "warn", "log", "transform"]
                    },
                    parameters: {
                        type: "object"
                    }
                },
                required: ["type"]
            }
        },
        enabled: {
            type: "boolean"
        }
    },
    required: ["name", "category", "priority", "expression", "enabled"],
    additionalProperties: false
} as const;

// Export all schemas for easy import
export const WorkflowSchemas = {
    ComplexProcess: ComplexProcessSchema,
    Signal: SignalSchema,
    Schedule: ScheduleSchema,
    Integration: IntegrationSchema,
    Policy: PolicySchema
} as const;

export type SchemaName = keyof typeof WorkflowSchemas;